import { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { MockProperty } from "@/data/mockProperties";

interface PropertyComparisonContextType {
  selectedProperties: MockProperty[];
  isSelected: (propertyId: string) => boolean;
  toggleProperty: (property: MockProperty) => void;
  clearAll: () => void;
  removeProperty: (propertyId: string) => void;
  maxSelections: number;
  canAddMore: boolean;
}

const PropertyComparisonContext = createContext<PropertyComparisonContextType | undefined>(undefined);

const MAX_SELECTIONS = 4;

export function PropertyComparisonProvider({ children }: { children: ReactNode }) {
  const [selectedProperties, setSelectedProperties] = useState<MockProperty[]>([]);

  const isSelected = useCallback((propertyId: string) => {
    return selectedProperties.some(p => p.id === propertyId);
  }, [selectedProperties]);

  const toggleProperty = useCallback((property: MockProperty) => {
    setSelectedProperties(prev => {
      const exists = prev.some(p => p.id === property.id);
      if (exists) {
        return prev.filter(p => p.id !== property.id);
      }
      if (prev.length >= MAX_SELECTIONS) {
        return prev; // Don't add if at max
      }
      return [...prev, property];
    });
  }, []);

  const clearAll = useCallback(() => {
    setSelectedProperties([]);
  }, []);

  const removeProperty = useCallback((propertyId: string) => {
    setSelectedProperties(prev => prev.filter(p => p.id !== propertyId));
  }, []);

  const canAddMore = selectedProperties.length < MAX_SELECTIONS;

  return (
    <PropertyComparisonContext.Provider value={{
      selectedProperties,
      isSelected,
      toggleProperty,
      clearAll,
      removeProperty,
      maxSelections: MAX_SELECTIONS,
      canAddMore,
    }}>
      {children}
    </PropertyComparisonContext.Provider>
  );
}

export function usePropertyComparison() {
  const context = useContext(PropertyComparisonContext);
  if (context === undefined) {
    throw new Error("usePropertyComparison must be used within a PropertyComparisonProvider");
  }
  return context;
}
